import { Button, Group, Radio, Stack, Text } from '@mantine/core'
import { closeAllModals, openModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  TExportMeasurementsToCSVFields,
  exportMeasurementsToCSV,
  exportMeasurementsToCSVSchema,
} from 'api/measurements/exportMeasurementsToCSV'
import { InputDatetimePicker } from 'components/form/input/Input_Datetime'
import dayjs from 'dayjs'
import { useFormMutation } from 'hooks/useFormMutation'
import { useState } from 'react'
import { FormProvider, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BsCalendar2Date } from 'react-icons/bs'
import { FaFileCsv } from 'react-icons/fa'
import { TiWeatherPartlySunny } from 'react-icons/ti'
import { capitalizeFirstLetter } from 'shared/utils'
import { HookFormError, IconButton, InputAsyncSelect, InputSubmit } from '..'
import { useGetIndicatorsList } from './DeviceMeasurementsFilters'
import { useGetDevice } from 'api/devices'

export const ExportMeasurementsToCSVButton = ({
  deviceId,
  indicatorCode,
  dateFrom,
  dateTo,
}: {
  deviceId: string
  indicatorCode?: string
  dateFrom?: string
  dateTo?: string
}) => {
  const { t } = useTranslation()
  const { data } = useGetDevice(deviceId)

  const onClick = () => {
    openModal({
      title: t('dashboard.exportToCSVFile'),
      size: 'lg',
      children: (
        <ExportMeasurementsToCSVForm
          mutationFn={exportMeasurementsToCSV({ deviceId })}
          indicatorCode={indicatorCode}
          dateFrom={dateFrom}
          dateTo={dateTo}
          deviceModelId={data?.modelId || ''}
        />
      ),
    })
  }

  return (
    <IconButton
      size={'lg'}
      onClick={onClick}
      icon={FaFileCsv}
      label={t('dashboard.exportToCSVFile')}
    />
  )
}

export const ExportMeasurementsToCSVForm = ({
  mutationFn,
  indicatorCode,
  dateFrom,
  dateTo,
  deviceModelId,
}: {
  mutationFn: (data: TExportMeasurementsToCSVFields) => Promise<unknown>
  indicatorCode?: string
  dateFrom?: string
  dateTo?: string
  deviceModelId: string
}) => {
  const { t } = useTranslation()

  const initialExportMode = 'single'
  const hasDateRange = !!(dateFrom && dateTo)
  const initialDateFrom = dateFrom || dayjs().subtract(2, 'month').toISOString()
  const initialDateTo = dateTo || dayjs().toISOString()

  const { methods, onSubmit } = useFormMutation({
    mutationFn,
    schema: exportMeasurementsToCSVSchema,
    onSuccess: () => {
      closeAllModals()
    },
    onError: () => {
      showNotification({
        color: 'red',
        message: t('generic.errorOccured'),
      })
    },
    initial: {
      indicatorCode,
      dateFrom: initialDateFrom,
      dateTo: initialDateTo,
      exportMode: initialExportMode,
    },
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <ExportMeasurementsToCSVFormFields
          deviceModelId={deviceModelId}
          initialExportMode={initialExportMode}
          showDateRangeNote={!hasDateRange}
        />
      </form>
    </FormProvider>
  )
}

export const ExportMeasurementsToCSVFormFields = ({
  deviceModelId,
  initialExportMode,
  showDateRangeNote,
}: {
  deviceModelId: string
  initialExportMode: 'all' | 'single'
  showDateRangeNote: boolean
}) => {
  const { t } = useTranslation()
  const { setValue } = useFormContext()
  const [exportMode, setExportMode] = useState<'all' | 'single'>(
    initialExportMode,
  )

  const indicatorHelper = useGetIndicatorsList({ deviceModelId })

  const handleModeChange = (value: string) => {
    const mode = value as 'all' | 'single'
    setExportMode(mode)
    setValue('exportMode', mode)
    if (mode === 'all') {
      setValue('indicatorCode', undefined)
    }
  }

  return (
    <Stack>
      {showDateRangeNote && (
        <Text size="sm" c="dimmed">
          {t('dashboard.maxDateRangeNote')}
        </Text>
      )}
      <Group>
        <InputDatetimePicker
          placeholder={capitalizeFirstLetter(t('generic.dateFrom'))}
          name="dateFrom"
          maxDate={dayjs().toDate()}
          clearable
          icon={<BsCalendar2Date />}
          popoverProps={{
            withinPortal: true,
            zIndex: 10000,
          }}
        />
        <InputDatetimePicker
          placeholder={capitalizeFirstLetter(t('generic.dateTo'))}
          name="dateTo"
          maxDate={dayjs().toDate()}
          clearable
          icon={<BsCalendar2Date />}
          popoverProps={{
            withinPortal: true,
            zIndex: 10000,
          }}
        />
      </Group>
      <Radio.Group
        value={exportMode}
        onChange={handleModeChange}
        label={capitalizeFirstLetter(t('sensor.indicators'))}
      >
        <Group mt="xs">
          <Radio value="single" label={t('generic.single')} />
          <Radio value="all" label={t('generic.all')} />
        </Group>
      </Radio.Group>
      {exportMode === 'single' && (
        <InputAsyncSelect
          name="indicatorCode"
          placeholder={capitalizeFirstLetter(t('sensor.indicator'))}
          label={capitalizeFirstLetter(t('sensor.indicator'))}
          icon={TiWeatherPartlySunny}
          {...indicatorHelper}
          clearable
          searchable
          withinPortal
          zIndex={10000}
        />
      )}
      <HookFormError />
      <Group justify="right">
        <Button variant="outline" onClick={() => closeAllModals()}>
          {t('generic.cancel')}
        </Button>
        <InputSubmit mt={0} value={t('generic.export')} />
      </Group>
    </Stack>
  )
}
